import {
  useState,
  useContext,
  useEffect,
  createContext,
  SetStateAction,
  Dispatch,
  useCallback,
  useRef,
} from 'react'

import * as React from 'react'
import { CouponType, ShortCourse } from '@type/course'
import { useAuth } from '@contexts/Auth'
import {
  LOCALSTORAGE_CART_KEY,
  LOCALSTORAGE_WISHLIST_KEY,
  LOCALSTORAGE_BUY_NOW_KEY,
  LOCALSTORAGE_SAVED_CART_ITEMS_KEY,
  LOCALSTORAGE_INACTIVE_CART_ITEMS_KEY,
  LOCALSTORAGE_CART_ITEM_TYPE_KEY,
} from '@constants/localstorageKeys'
import {
  getUserCart,
  addUserCart,
  updateUserCart,
  deleteUserCart,
  getUserWishlist,
  addUserWishlist,
  updateUserWishlist,
  deleteUserWishlist,
} from '@services/api/user'
import { difference } from '@helpers/difference'
import isEmpty from 'lodash/isEmpty'
import validateCoupon from '@services/api/payment/validateCoupon'
import validateDiscount from '@services/api/payment/validateDiscount'
import getUserSavedItems from '@services/api/user/getUserSavedItems'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import ToastMessage from '@components/molecules/ToastMessage'
import WishlistIcon from '@public/static/icons/headerIcons/wishlist-icon'
import { colors } from '@configs/styles/config'
import getGlobalDiscount from '@services/api/course/global-discounts/getGlobalDiscount'
import PricingPolicy from '@type/pricingPolicy'
import { useRouter } from 'next/router'
// import Image from 'next/image'
// import { Modal, ModalContent, useDisclosure } from '@nextui-org/react-latest'
import getCartSummary, {
  CartSummaryResponse,
} from '@services/api/payment/cartSummary'
import Gift from '@type/order/gift'
import createGiftCardDetails from '@services/api/gift/createGiftCardDetails'
import { useDisclosure } from '@nextui-org/react-latest'
import useGetUserCart from '@services/react-query/hooks/courses/queries/useGetUserCart'
import useGteUserWishlist from '@services/react-query/hooks/courses/queries/useGetUserWishlist'
import useGetSavedItems from '@services/react-query/hooks/courses/queries/useGetSavedItems'
import { queryClient } from 'pages/_app'

type CartAndWishListContext = {
  cartItems: ShortCourse[]
  cartItemType: 'Course' | 'Gift_card' | null
  setCartItemType: Dispatch<SetStateAction<'Course' | 'Gift_card' | null>>
  addToCart: (
    item: ShortCourse,
    itemType?: 'Course' | 'Gift_card',
    clearInstantCheckoutItem?: boolean
  ) => void
  removeFromCart: (itemId: string[] | string) => void
  inactiveCartItems: string[]
  toggleAllCartItemsState: (value: boolean) => void
  toggleCartItemState: (itemId: string) => void
  wishlistItems: ShortCourse[]
  addToWishlist: (item: ShortCourse) => void
  removeFromWishlist: (itemId: string) => void
  coupons: CouponType[]
  addCoupon: (coupon: string) => Promise<void>
  discounts: any[]
  checkDiscount: (code: string, items?: string[]) => Promise<any>
  addDiscounts: (code: string, items?: string[]) => Promise<void>
  replaceDiscount: (oldDiscount: string, newDiscount: any) => Promise<void>
  removeDiscount: (code: string) => Promise<void>
  getCartTotal: () => {
    priceSum: number
    regularPrice: number
  }
  updateCartAndWishlist: Function
  getCartTotalDiscount: () => number
  getPayableAmount: () => number
  giftDetails: any
  setGiftDetails: any
  instantCheckoutItem: ShortCourse | any | null
  setInstantCheckoutItem: Dispatch<SetStateAction<ShortCourse | any | null>>
  updateCartAndWishlistFromAPI: () => void
  getReferralCodeFromLocalStorage: () => string | null
  storeReferralCodeInLocalStorage: (code: string) => void
  getGlobalDiscountAmount: () => number
  isGlobalDiscountApplied: boolean
  setSelectedGiftCard: React.Dispatch<React.SetStateAction<Gift | undefined>>
  selectedGiftCard: Gift | undefined
  giftCardAmount: number
  setGiftCardAmount: React.Dispatch<React.SetStateAction<number>>
  setCartSummary: React.Dispatch<React.SetStateAction<CartSummaryResponse>>
  cartSummary: CartSummaryResponse
  setGiftCardItem: React.Dispatch<React.SetStateAction<string>>
  giftCardItem: string
  isLoadingCartSummary: boolean
  isRemoving: RemovingState
}

// const pathsToShowPopup = ['/', '/courses/[course_id]']
const makeTopBarSticky = [
  '/create-course/[...slug]',
  '/author',
  '/referrals/dashboard',
  '/student',
]

const allowedPathsForTopBar = [
  '/',
  '/courses/[course_id]',
  '/courses',
  '/cart',
  '/wishlist',
  '/my-courses',
]

const INITIAL_CART = {
  totalAmount: 0,
  totalDiscount: 0,
  amountToBePaidByGiftCard: 0,
  amountToBePaidByCreditCard: 0,
}

const Index = createContext({} as CartAndWishListContext)
const useCartAndWishList = () => useContext(Index)

interface RemovingState {
  item: string
  type: 'cart' | 'wishlist' | ''
}

const INITIAL_REMOVING_STATE: RemovingState = { item: '', type: '' }
const CartAndWishlistProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth()
  const { t } = useTranslation(['common', 'wishlist', 'footer'])
  const [cartItemType, setCartItemType] = useState<
    'Course' | 'Gift_card' | null
  >(null)
  const [cartItems, setCartItems] = useState<ShortCourse[]>([])
  // Mirror of cartItems that is always current (updated synchronously on every
  // setCartItems below). Lets rapid, back-to-back mutations read the latest
  // cart instead of a stale render-time closure — the cause of removed courses
  // "coming back" when several are removed in quick succession.
  const cartItemsRef = useRef<ShortCourse[]>([])
  const [inactiveCartItems, setInactiveCartItems] = useState<string[]>([])
  const [savedCartItems, setSavedCartItems] = useState<ShortCourse[]>([])
  const [wishlistItems, setWishlistItems] = useState<ShortCourse[]>([])
  const [giftDetails, setGiftDetails] = useState<any>(null)
  const [coupons, setCoupons] = useState<CouponType[]>([])
  const [discounts, setDiscounts] = useState<any[]>([])
  const [instantCheckoutItem, setInstantCheckoutItem] = useState<
    ShortCourse | any | null
  >(null)
  const [globalDiscount, setGlobalDiscount] = useState<PricingPolicy | null>(
    null
  )
  const {
    // isOpen,
    // onOpen,
    // onOpenChange
  } = useDisclosure()
  const [selectedGiftCard, setSelectedGiftCard] = useState<Gift | undefined>(
    undefined
  )
  const [giftCardAmount, setGiftCardAmount] = useState(0)
  const [giftCardItem, setGiftCardItem] = useState('')
  const [referralDiscount, setReferralDiscount] =
    useState<PricingPolicy | null>(null)
  const [isLoadingReferralCode, setIsLoadingReferralCode] = useState(false)
  const [cartSummary, setCartSummary] =
    useState<CartSummaryResponse>(INITIAL_CART)
  const [isLoadingCartSummary, setIsLoadingCartSummary] = useState(false)
  const [triggerCartSummary, setTriggerCartSummary] = useState(false)
  const [isLoadingGlobalDiscount, setIsLoadingGlobalDiscount] = useState(false)
  const [isRemoving, setIsRemoving] = useState<RemovingState>(
    INITIAL_REMOVING_STATE
  )
  const { data: cartData } = useGetUserCart(isAuthenticated)
  const { data: wishlistData } = useGteUserWishlist(isAuthenticated)
  const { data: savedItemsData } = useGetSavedItems(isAuthenticated)
  const cartSummaryController = useRef<AbortController>(undefined)
  const router = useRouter()

  // Keep cartItemsRef in sync with cartItems for every code path that updates
  // the cart (add, server hydration, restore, etc.). removeFromCart also writes
  // the ref synchronously to handle rapid same-tick removals before this effect
  // runs.
  useEffect(() => {
    cartItemsRef.current = cartItems
  }, [cartItems])

  // Restore "Buy now' & 'Selected Cart' items
  useEffect(() => {
    // if (pathsToShowPopup.includes(router.pathname)) onOpen()

    const localBuyNowItem = localStorage.getItem(LOCALSTORAGE_BUY_NOW_KEY)
    const localInactiveCartItems = localStorage.getItem(
      LOCALSTORAGE_INACTIVE_CART_ITEMS_KEY
    )

    if (localBuyNowItem && router.pathname !== '/cart') {
      const items = JSON.parse(localBuyNowItem)
      // If courseId is in URL query, use that specific course; otherwise use the last one
      const courseIdFromUrl = router.query.courseId as string
      if (courseIdFromUrl && Array.isArray(items)) {
        const specificCourse = items.find(
          (item) => item._id === courseIdFromUrl
        )
        setInstantCheckoutItem(specificCourse || items[items.length - 1])
      } else {
        setInstantCheckoutItem(
          Array.isArray(items) ? items[items.length - 1] : items
        )
      }
    }
    localInactiveCartItems &&
      setInactiveCartItems(JSON.parse(localInactiveCartItems))
  }, [router.query.courseId, router.pathname])

  useEffect(() => {
    const localCart = localStorage.getItem(LOCALSTORAGE_CART_KEY)
    const localWishlist = localStorage.getItem(LOCALSTORAGE_WISHLIST_KEY)
    const localSavedCartItems = localStorage.getItem(
      LOCALSTORAGE_SAVED_CART_ITEMS_KEY
    )
    if (!isAuthenticated) {
      localCart && setCartItems(JSON.parse(localCart))
      localWishlist && setWishlistItems(JSON.parse(localWishlist))
      localSavedCartItems && setSavedCartItems(JSON.parse(localSavedCartItems))
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (cartData && wishlistData && savedItemsData) {
      updateCartAndWishlist({
        cart: cartData,
        wishlist: wishlistData,
        savedItems: savedItemsData,
      })
    }
  }, [cartData, wishlistData, savedItemsData])

  useEffect(() => {
    const localCartItemType = localStorage.getItem(
      LOCALSTORAGE_CART_ITEM_TYPE_KEY
    )
    const localCart = localStorage.getItem(LOCALSTORAGE_CART_KEY)
    const localBuyNowItem = localStorage.getItem(LOCALSTORAGE_BUY_NOW_KEY)

    if (cartItemType) {
      localStorage.setItem(LOCALSTORAGE_CART_ITEM_TYPE_KEY, cartItemType)
    } else if (localCart && !localBuyNowItem) {
      setCartItemType('Course')
      localStorage.setItem(LOCALSTORAGE_CART_ITEM_TYPE_KEY, 'Course')
    } else if (localCartItemType) {
      setCartItemType(localCartItemType as any)
    } else {
      localStorage.removeItem(LOCALSTORAGE_CART_ITEM_TYPE_KEY)
    }
  }, [cartItemType])

  useEffect(() => {
    if (instantCheckoutItem) {
      // Store as an array of items instead of single item
      const existingItems = localStorage.getItem(LOCALSTORAGE_BUY_NOW_KEY)
      const items = existingItems ? JSON.parse(existingItems) : []

      // Remove if item already exists (to avoid duplicates), then add new one
      const filteredItems = Array.isArray(items)
        ? items.filter((item) => item._id !== instantCheckoutItem._id)
        : []

      localStorage.setItem(
        LOCALSTORAGE_BUY_NOW_KEY,
        JSON.stringify([...filteredItems, instantCheckoutItem])
      )
    }
  }, [instantCheckoutItem])

  const tmpCartItems =
    !!cartItems && Array.isArray(cartItems)
      ? cartItems
          ?.filter((c) => !inactiveCartItems?.includes(c?._id))
          ?.map((i) => i?._id)
          ?.join('-')
      : ''

  const isCartItemsValid = Array.isArray(cartItems) && cartItems?.length > 0

  const fetchCartSummary = useCallback(async () => {
    if (cartSummaryController.current) cartSummaryController.current?.abort()
    console.log(cartItemType, instantCheckoutItem, cartItems.length)

    if (!cartItemType || (cartItems?.length === 0 && !instantCheckoutItem)) {
      setIsLoadingCartSummary(false)
      setCartSummary(INITIAL_CART)
      return
    }

    const controller = new AbortController()
    cartSummaryController.current = controller

    setIsLoadingCartSummary(true)
    const referralDiscount = discounts?.find(
      (d) => d.discountType === 'referral'
    )
    try {
      let giftCardItemId = ''
      if (cartItemType === 'Gift_card') {
        const response = await createGiftCardDetails(
          instantCheckoutItem.price,
          {
            name: instantCheckoutItem.senderName,
            email: instantCheckoutItem.senderEmail,
          },
          {
            name: instantCheckoutItem.recipientName,
            email: instantCheckoutItem.recipientEmail,
          },
          instantCheckoutItem.giftMessage,
          instantCheckoutItem.deliveryMethod,
          instantCheckoutItem.deliveryDate,
          instantCheckoutItem.isAnonymous,
          instantCheckoutItem.designType
        )
        if (response) {
          giftCardItemId = response._id
          setGiftCardItem(giftCardItemId)
        }
      }

      const contextCartItems = instantCheckoutItem
        ? [instantCheckoutItem]
        : cartItems
      const items =
        cartItemType === 'Gift_card'
          ? [giftCardItemId]
          : contextCartItems
              .filter((c) => !inactiveCartItems.includes(c._id))
              .map((i) => i._id)

      // Call the API directly rather than through queryClient.fetchQuery.
      // Because we deliberately abort superseded requests, routing them through
      // react-query's cache can leave a *canceled* (rejected) promise cached
      // under the queryKey; a retrigger with the same key then resolves to that
      // canceled promise instead of refetching, stranding the summary at 0.
      // This race is timing-dependent (hence it reproduces on some machines but
      // not others). Our own AbortController + superseding check below already
      // dedupes in-flight requests.
      const res = await getCartSummary(
        items,
        cartItemType === 'Gift_card'
          ? []
          : discounts
              .filter((d) => d.discountType === 'policy')
              .map((discount) => discount._id),
        cartItemType,
        selectedGiftCard?._id,
        (giftCardAmount || 0) * 100,
        referralDiscount?.code,
        undefined,
        controller.signal
      )

      // A newer fetchCartSummary call may have started (and aborted this
      // controller) while we were awaiting. If so, this response is stale —
      // don't apply it or clear the loading flag, otherwise we'd strand the
      // summary at the old/zero value and override the newer in-flight request.
      if (cartSummaryController.current !== controller) return

      const tempCartSummary =
        cartItems?.length === 0 && !instantCheckoutItem ? INITIAL_CART : res
      setCartSummary(tempCartSummary)
      setIsLoadingCartSummary(false)
    } catch (error) {
      // Aborts are expected when a newer request supersedes this one; the newer
      // request owns the loading flag and final state, so leave both alone.
      const isAbort =
        (error as { name?: string })?.name === 'CanceledError' ||
        (error as { name?: string })?.name === 'AbortError' ||
        (error as { code?: string })?.code === 'ERR_CANCELED'
      if (isAbort || cartSummaryController.current !== controller) return

      setIsLoadingCartSummary(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cartItemType,
    instantCheckoutItem,
    discounts,
    selectedGiftCard?._id,
    giftCardAmount,
    tmpCartItems,
    triggerCartSummary,
  ])

  useEffect(() => {
    if (!isLoadingGlobalDiscount) fetchCartSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tmpCartItems,
    discounts,
    selectedGiftCard,
    giftCardAmount,
    cartItemType,
    instantCheckoutItem?._id,
    triggerCartSummary,
  ])

  const fetchGlobalDiscount = async () => {
    setIsLoadingGlobalDiscount(true)
    try {
      const res = await getGlobalDiscount()

      setDiscounts((prev) => [...prev, { ...res, discountType: 'policy' }])
      setGlobalDiscount({ ...res, discountType: 'policy' })
      setIsLoadingGlobalDiscount(false)
    } catch (error) {
      setIsLoadingGlobalDiscount(false)
      if ((error as { status: number }).status === 404) {
        setTimeout(() => {
          setTriggerCartSummary((prev) => !prev)
        }, 50)
      }
    }
  }

  useEffect(() => {
    fetchGlobalDiscount()
  }, [])

  useEffect(() => {
    if (!globalDiscount && discounts.find((d) => d.isGlobal)) {
      setGlobalDiscount(discounts.find((d) => d.isGlobal))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discounts])

  useEffect(() => {
    if (
      giftDetails &&
      discounts.length > 0 &&
      !discounts.find((d) => d.isGlobal)
    )
      setDiscounts([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftDetails])

  useEffect(() => {
    if (cartItemType === 'Gift_card' && discounts.find((d) => d.isGlobal)) {
      setDiscounts((prev) => prev.filter((d) => !d.isGlobal))
    }
    if (
      cartItemType === 'Course' &&
      !discounts.find((d) => d.isGlobal) &&
      globalDiscount &&
      discounts.length === 0
    ) {
      setDiscounts([globalDiscount])
    }
  }, [cartItemType, globalDiscount])

  useEffect(() => {
    localStorage.setItem(
      LOCALSTORAGE_INACTIVE_CART_ITEMS_KEY,
      JSON.stringify(inactiveCartItems)
    )
  }, [inactiveCartItems])

  useEffect(() => {
    if (referralDiscount && globalDiscount) {
      const updatedItems = discounts.filter((d) => !d.isGlobal)
      setDiscounts(updatedItems)
    }
  }, [referralDiscount, globalDiscount])

  const storeReferralCodeInLocalStorage = (code: string) => {
    const expiryDate = new Date().getTime() + 24 * 60 * 60 * 1000 // current time + 24 hours
    const discountData = {
      code,
      expiry: expiryDate,
    }
    localStorage.setItem('referral-code', JSON.stringify(discountData))
  }

  const getReferralCodeFromLocalStorage: () => string | null = () => {
    const discountData = localStorage.getItem('referral-code')
    if (discountData) {
      const { code, expiry } = JSON.parse(discountData)
      if (new Date().getTime() < expiry) {
        return code
      } else {
        localStorage.removeItem('referral-code')
      }
    }
    return null
  }

  useEffect(() => {
    if (isCartItemsValid || instantCheckoutItem || router.query?.ref) {
      const code =
        (router.query.ref as string) ||
        (router.query.code as string) ||
        getReferralCodeFromLocalStorage()
      if (code) {
        if (
          discounts.find((d) => d?.code === code || d?.referralCode === code) ||
          isLoadingReferralCode
        )
          return
        setIsLoadingReferralCode(true)
        checkDiscount(code)
          .then(async (details) => {
            if (details) {
              setReferralDiscount(details)
              await addDiscounts(code)
            }
          })
          .catch()
          .finally(() => {
            setIsLoadingReferralCode(false)
          })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, instantCheckoutItem, router.query?.ref])

  const updateCartAndWishlist = async ({ cart, wishlist, savedItems }) => {
    const localCart = localStorage.getItem(LOCALSTORAGE_CART_KEY)
    const localWishlist = localStorage.getItem(LOCALSTORAGE_WISHLIST_KEY)
    const localCartParsedUnfiltered: ShortCourse[] =
      JSON.parse(localCart || '[]') || []
    const localCartParsed: ShortCourse[] = Array.isArray(
      localCartParsedUnfiltered
    )
      ? localCartParsedUnfiltered
      : []
    if (
      localCartParsed &&
      localCartParsed?.length &&
      !isEmpty(difference(localCartParsed, cart))
    ) {
      //const updatedCartItems = difference(localCartParsed.map(item => item?._id), cart.map(item => item?._id)).concat(cart);
      const updatedCartItems = localCartParsed
        ?.filter(
          (item) =>
            !(Array.isArray(cart) ? cart : [])
              .map((itm) => itm._id)
              ?.includes(item._id)
        )
        .concat(cart)
      if (!isEmpty(difference(updatedCartItems, cart))) {
        updateUserCart(updatedCartItems.map((itm) => itm._id)).then((items) => {
          setCartItems(items)
          localStorage.setItem(LOCALSTORAGE_CART_KEY, JSON.stringify(items))
          queryClient.setQueryData(['user-cart'], items)

          return true
        })
      }
    }
    const localWishlistParsedUnfiltered: ShortCourse[] =
      JSON.parse(localWishlist || '[]') || []
    const localWishlistParsed: ShortCourse[] = Array.isArray(
      localWishlistParsedUnfiltered
    )
      ? localWishlistParsedUnfiltered
      : []

    if (
      localWishlistParsed &&
      localWishlistParsed?.length &&
      !isEmpty(difference(localWishlistParsed, wishlist))
    ) {
      //const updatedWishlistItems = difference(localWishlistParsed.map(item => item?._id), wishlist.map(item => item?._id)).concat(wishlist);
      const updatedWishlistItems = localWishlistParsed
        ?.filter(
          (item) =>
            !(Array.isArray(wishlist) ? wishlist : [])
              .map((itm) => itm._id)
              ?.includes(item._id)
        )
        .concat(wishlist)
      if (!isEmpty(difference(updatedWishlistItems, wishlist))) {
        updateUserWishlist(updatedWishlistItems.map((itm) => itm._id)).then(
          (items) => {
            setWishlistItems(items)
            localStorage.setItem(
              LOCALSTORAGE_WISHLIST_KEY,
              JSON.stringify(items)
            )
            queryClient.setQueryData(['user-wishlist'], items)
            return true
          }
        )
      }
    }

    setCartItems(cart)
    localStorage.setItem(LOCALSTORAGE_CART_KEY, JSON.stringify(cart))
    setWishlistItems(wishlist)
    localStorage.setItem(LOCALSTORAGE_WISHLIST_KEY, JSON.stringify(wishlist))
    setSavedCartItems(savedItems)
    localStorage.setItem(
      LOCALSTORAGE_SAVED_CART_ITEMS_KEY,
      JSON.stringify(savedItems)
    )
  }

  const updateCartAndWishlistFromAPI = async () => {
    getCartAndWishlist()
      .then(({ cart, wishlist, savedItems }) => {
        setCartItems(cart)
        localStorage.setItem(LOCALSTORAGE_CART_KEY, JSON.stringify(cart))
        setWishlistItems(wishlist)
        localStorage.setItem(
          LOCALSTORAGE_WISHLIST_KEY,
          JSON.stringify(wishlist)
        )
        setSavedCartItems(savedItems)
        localStorage.setItem(
          LOCALSTORAGE_SAVED_CART_ITEMS_KEY,
          JSON.stringify(savedItems)
        )
      })
      .catch()
  }

  const getCartAndWishlist = async () => {
    const [cart, wishlist, savedItems] = await Promise.all([
      getUserCart(),
      getUserWishlist(),
      getUserSavedItems(),
    ])
    return { cart, wishlist, savedItems }
  }

  const addToCart = async (
    item: ShortCourse,
    itemType: 'Course' | 'Gift_card' = 'Course',
    clearInstantCheckoutItem = true
  ) => {
    if (
      cartItems?.filter((cartItem) => cartItem?._id === item?._id)?.length == 0
    ) {
      const items = [...cartItems, item]
      setCartItemType(itemType)
      setCartItems(items)
      localStorage.setItem(LOCALSTORAGE_CART_KEY, JSON.stringify(items))
      if (isAuthenticated) {
        await addUserCart(item._id)
        queryClient.setQueryData(['user-cart'], items)
      }
      if (clearInstantCheckoutItem) setInstantCheckoutItem(null)
    }
  }

  const removeFromCart = async (ids: string[] | string) => {
    const itemIds = Array.isArray(ids) ? ids : [ids]
    // Derive the next cart from cartItemsRef (always current), not the
    // render-time `cartItems` closure. Two quick removals would otherwise both
    // filter the same stale snapshot, so the second would write back a list
    // that still contains the first removed course — making it "come back".
    // We update the ref synchronously so a follow-up removal in the same tick
    // sees this removal's result.
    const items = cartItemsRef.current.filter(
      (item) => !itemIds.includes(item?._id)
    )
    cartItemsRef.current = items
    setCartItems(items)
    localStorage.setItem(LOCALSTORAGE_CART_KEY, JSON.stringify(items))
    setInactiveCartItems((state) => state.filter((s) => !itemIds.includes(s)))

    const activeDiscounts = discounts.filter((d) =>
      d.courses?.some((courseId) => itemIds.includes(courseId))
    )
    if (activeDiscounts.length > 0) {
      for (const discount of activeDiscounts) {
        await removeDiscount(discount.code)
      }
    }
    if (isAuthenticated) {
      setIsRemoving({ type: 'cart', item: itemIds[0] })
      try {
        await Promise.all(itemIds.map((id) => deleteUserCart(id)))
        // Sync the react-query cache to the latest ref (not this call's stale
        // `items`) so a concurrent removal's result isn't clobbered, which
        // would re-introduce a removed course on the next cache-driven sync.
        queryClient.setQueryData(['user-cart'], cartItemsRef.current)
      } catch (error) {
      } finally {
        setIsRemoving(INITIAL_REMOVING_STATE)
      }
    }
    setInstantCheckoutItem(null)

    toast(({ closeToast, toastProps }) => (
      <ToastMessage
        closeToast={closeToast}
        toastProps={toastProps}
        title={t('messages.removedFromCart')}
      />
    ))
  }

  const toggleAllCartItemsState = async (value: boolean) => {
    setInactiveCartItems(value ? [] : cartItems.map((c) => c._id))
  }
  const toggleCartItemState = async (itemId: string) => {
    const exists = inactiveCartItems.includes(itemId)
    setInactiveCartItems((state) =>
      exists ? state.filter((s) => s !== itemId) : [...state, itemId]
    )
  }
  const addToWishlist = async (item: ShortCourse) => {
    if (
      wishlistItems?.filter((wishlistItem) => wishlistItem?._id === item?._id)
        ?.length == 0
    ) {
      const items = [...wishlistItems, item]
      setWishlistItems(items)
      localStorage.setItem(LOCALSTORAGE_WISHLIST_KEY, JSON.stringify(items))

      toast(({ closeToast, toastProps }) => (
        <ToastMessage
          closeToast={closeToast}
          toastProps={toastProps}
          title={t('courseHasBeenAddedToYourWishlist', {
            ns: 'wishlist',
          })}
          icon={<WishlistIcon color={colors.uguWhite} />}
        />
      ))
      if (isAuthenticated) {
        await addUserWishlist(item._id)
        queryClient.setQueryData(['user-wishlist'], items)
      }
    }
  }

  const removeFromWishlist = async (itemId: string) => {
    const items = wishlistItems.filter((item) => item?._id !== itemId)
    setWishlistItems(items)
    localStorage.setItem(LOCALSTORAGE_WISHLIST_KEY, JSON.stringify(items))
    if (isAuthenticated) {
      setIsRemoving({ type: 'wishlist', item: itemId })
      try {
        await deleteUserWishlist(itemId)
        queryClient.setQueryData(['user-wishlist'], items)
      } catch (error) {
      } finally {
        setIsRemoving(INITIAL_REMOVING_STATE)
      }
    }
  }

  const getCartTotal = () => {
    let priceSum = 0
    let regularPrice = 0
    if (!Array.isArray(cartItems))
      return {
        priceSum,
        regularPrice,
      }
    cartItems
      ?.filter((c) => !inactiveCartItems.includes(c._id))
      ?.forEach((item) => {
        priceSum =
          priceSum +
          parseInt(
            item?.salePrice?.toString() || item?.price?.toString() || '0',
            10
          )
        regularPrice =
          regularPrice +
          parseInt(
            item?.salePrice?.toString() ? item?.price?.toString() : '0',
            10
          )
      })

    return { priceSum, regularPrice }
  }

  const getCartWithDiscountTotal = () => {
    let priceSum = 0
    let salesPriceSum = 0
    if (instantCheckoutItem) {
      return {
        price: +instantCheckoutItem?.price || 0,
        salePrice: +instantCheckoutItem?.salePrice || 0,
      }
    }
    cartItems
      ?.filter((c) => !inactiveCartItems.includes(c._id))
      ?.forEach((item) => {
        priceSum = priceSum + parseInt(item?.price?.toString() || '0', 10)
        salesPriceSum =
          salesPriceSum + parseInt(item?.salePrice?.toString() || '0', 10)
      })

    return { price: priceSum, salePrice: salesPriceSum }
  }

  const getCartTotalDiscount = () => {
    let discountSum = 0
    let discountPercentSum = 0

    discounts?.forEach(async (discount) => {
      if (discount.discountType === 'policy') {
        if (discount.courses) {
          const courseId = discount.courses.find((item: string) => {
            if (instantCheckoutItem && instantCheckoutItem._id === item) {
              return item
            }
            return cartItems
              ?.filter((c) => !inactiveCartItems.includes(c._id))
              ?.map((i) => i._id)
              .includes(item)
          })
          const course = instantCheckoutItem
            ? instantCheckoutItem._id === courseId
              ? instantCheckoutItem
              : null
            : cartItems.find((item) => item._id === courseId)
          if (course) {
            if (discount.valueType === 'fixed') {
              if (course.salePrice) {
                const discountValue = +discount.value
                discountSum = discountSum + discountValue
              } else {
                const discountValue = +discount.value
                discountSum = discountSum + discountValue
              }
            } else if (discount.valueType === 'percentage') {
              if (course.salePrice) {
                const discountValue = parseFloat(
                  (
                    (discount.value / 100) *
                    ((course?.salePrice +
                      (globalDiscount
                        ? course?.price - course?.salePrice
                        : 0) || 0) /
                      100)
                  ).toFixed()
                )
                discountSum = discountSum + discountValue
              } else {
                const discountValue =
                  (discount.value / 100) * (course.price / 100)
                discountSum = discountSum + discountValue
              }
            }
          }
        }
      } else {
        discountPercentSum += discount.discountPercentage
      }
    })

    const referralDiscountSum =
      (getCartTotal().priceSum / 100) * discountPercentSum
    return discountSum + referralDiscountSum
  }

  const getGlobalDiscountAmount = () => {
    const { price, salePrice } = getCartWithDiscountTotal()
    if (!globalDiscount || !price || cartItemType === 'Gift_card') return 0
    return price - salePrice
  }

  const getPayableAmount = (): number => {
    return getCartTotal().priceSum - getCartTotalDiscount()
  }

  const addCoupon = (coupon: string): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await validateCoupon(coupon, cartItems)
        if (response) {
          setCoupons((state) => [...state, response])
          resolve()
        } else {
          reject()
        }
      } catch (err) {
        reject(err)
      }
    })
  }
  const checkDiscount = (code: string, items?: string[]): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        const currentItems = items
          ? items
          : instantCheckoutItem
            ? [instantCheckoutItem]
            : cartItems.filter((c) => !inactiveCartItems.includes(c._id))

        const response = await validateDiscount(
          code,
          currentItems.map((i) => i._id),
          cartItemType === 'Gift_card' || giftDetails ? true : false
        )
        const referralUser = response?.referralUser?.referralCodes?.find(
          (c) => c?.code === code
        )
        resolve(
          response?.policy
            ? { ...response.policy, discountType: response.type }
            : {
                ...response.referralUser,
                ...referralUser,
                value: referralUser.discountPercentage * 100,

                discountType: response.type,
              }
        )
      } catch (err) {
        reject(err)
      }
    })
  }
  const addDiscounts = (code: string, items?: string[]): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await checkDiscount(code, items)
        if (response) {
          setDiscounts((state) => [...state, response])
          resolve()
        } else {
          reject()
        }
      } catch (err) {
        reject(err)
      }
    })
  }

  const removeDiscount = (code: string): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        setDiscounts((state) => state.filter((s) => s.code !== code))
        resolve()
      } catch (err) {
        reject(err)
      }
    })
  }

  const replaceDiscount = (
    oldDiscount: string,
    newDiscount: string
  ): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        setDiscounts((state) => [
          ...state.filter((s) => s.code !== oldDiscount),
          newDiscount,
        ])
        resolve()
      } catch (err) {
        reject(err)
      }
    })
  }

  const contextValues = {
    cartItems: Array.isArray(cartItems) ? cartItems : [],
    cartItemType,
    setCartItemType,
    addToCart,
    removeFromCart,
    inactiveCartItems,
    toggleAllCartItemsState,
    toggleCartItemState,
    savedCartItems: Array.isArray(savedCartItems) ? savedCartItems : [],
    setSavedCartItems,
    wishlistItems: Array.isArray(wishlistItems) ? wishlistItems : [],
    addToWishlist,
    removeFromWishlist,
    discounts,
    checkDiscount,
    addDiscounts,
    removeDiscount,
    replaceDiscount,
    coupons,
    addCoupon,
    getCartTotal,
    updateCartAndWishlist,
    getCartTotalDiscount,
    getPayableAmount,
    giftDetails,
    setGiftDetails,
    instantCheckoutItem,
    setInstantCheckoutItem,
    updateCartAndWishlistFromAPI,
    storeReferralCodeInLocalStorage,
    getReferralCodeFromLocalStorage,
    getGlobalDiscountAmount,
    isGlobalDiscountApplied:
      globalDiscount && discounts.find((discount) => discount?.isGlobal),
    setSelectedGiftCard,
    selectedGiftCard,
    giftCardAmount,
    setGiftCardAmount,
    setCartSummary,
    cartSummary,
    setGiftCardItem,
    giftCardItem,
    isLoadingCartSummary: isLoadingCartSummary,
    isRemoving,
  }

  const isTopBarHidden = true

  return (
    <Index.Provider value={contextValues}>
      {allowedPathsForTopBar.includes(router.pathname) && !isTopBarHidden && (
        <TopSaleBar />
      )}
      {children}
      {/* {isOpen && (
        <Modal
          size="2xl"
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          placement="center"
          scrollBehavior="normal"
          classNames={{
            backdrop: 'bg-uguPurple/60',
          }}
        >
          <ModalContent className="bg-transparent shadow-none">
            <Image
              src="/static/images/christmas.png"
              alt=""
              width={800}
              height={800}
              className="w-full h-full"
            />
          </ModalContent>
        </Modal>
      )} */}
    </Index.Provider>
  )
}

export { useCartAndWishList, CartAndWishlistProvider }

const TopSaleBar = ({}: { onOpen?: () => void }) => {
  const targetDateISO = '2026-04-24T00:00:00' // Easter sale end date
  const [timeRemaining, setTimeRemaining] = useState<{
    hours: number
    days: number
    minutes: number
    seconds: number
  } | null>(null)
  const router = useRouter()

  useEffect(() => {
    const targetDate = new Date(targetDateISO)

    function calculateTimeRemaining() {
      const now = new Date()
      const difference =
        (targetDate as unknown as number) - (now as unknown as number)

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 } // Countdown ended
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24))
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24)
      const minutes = Math.floor((difference / (1000 * 60)) % 60)
      const seconds = Math.floor((difference / 1000) % 60)

      return { days, hours, minutes, seconds }
    }

    setTimeRemaining(calculateTimeRemaining())

    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining())
    }, 1000) // Update every second

    return () => clearInterval(timer) // Clean up on component unmount
  }, [targetDateISO])

  const isHidden = makeTopBarSticky.includes(router.pathname)

  if (isHidden) return <></>

  return (
    <div
      className="md:flex w-full text-center z-50 justify-center md:py-5 py-3 items-center gap-1 px-3 text-black text-sm tracking-[0.02em]"
      onClick={() => {
        router.push('/shop/gift-card/')
      }}
      style={{
        fontFamily: 'OutfitLight',
        backgroundColor: '#fff3cd',
      }}
    >
      <span
        className="text-sm tracking-[0.02em] min-h-[24px] inline min-w-max"
        style={{ fontFamily: 'OutfitMedium' }}
      >
        {'🐣 Easter Sale'}
      </span>{' '}
      Hop into learning this Easter! Up to 50% Off on all courses, starting at
      $11.99!
      {timeRemaining && ` Ends in `}
      {timeRemaining && (
        <span
          className="text-white text-sm tracking-[0.02em] bg-red-500 rounded-lg shadow px-2 py-0.5"
          style={{ fontFamily: 'OutfitMedium', backgroundColor: colors.uguRed }}
        >{`${timeRemaining?.days}d : ${timeRemaining?.hours}h : ${timeRemaining?.minutes}m : ${timeRemaining?.seconds}s`}</span>
      )}
    </div>
  )
}
